import { formatRecordedMoney } from "utils/recordedMoney";
import useCurrencies from "api/CurrencyResource/useCurrencies";
import usePaymentMethodAvailability from "api/PaymentsResource/usePaymentMethodAvailability";
import useProfile from "api/UserResource/useProfile";
import React from "react";
import AccountLayout from "../AccountLayout";
import {
	activePaymentMethods,
	priceInCurrency,
	getPaymentMethodLabel,
} from "utils";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import AccountField from "../AccountField";
import useFetchSubscriptions from "api/SubscriptionsResource/useFetchSubscriptions";
import useApplyAutoDeduction from "api/UserResource/useApplyAutoDeduction";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useCancelAutoDeductions from "api/UserResource/useCancelAutoDeductions";
import AccountPage from "../AccountPage";

const AutoDeductions = () => {
    const { data: catalog = [] } = useCurrencies();
    const { data: methods } = usePaymentMethodAvailability();
	const { data: subscriptions, isLoading: isLoadingSubscriptions } = useFetchSubscriptions();
	const { data: profile, isLoading: isLoadingProfile } = useProfile();

	const recurringSubscription = profile?.data?.recurred_subscription;

	const subscriptionChoices =
		subscriptions?.data?.map((subscription) => ({
			label: `${subscription?.name} - (${subscription?.duration} ${subscription?.time_unit})`,
			value: subscription.id,
		})) || [];

	const currencies = catalog.filter((currency) => {
        const method = currency.code === "USD" ? "Ecocash USD" : currency.code === "ZWL" ? "Ecocash" : null;
        return method && methods?.[method]?.enabled && methods[method].supported_currencies?.includes(currency.code);
    }).map((currency) => ({ label: `${currency.label} (${currency.code})`, value: currency.code }));

	const initialValues = {
		subscription: "",
		currency: "",
		mobile_number: "",
	};

	const validate = Yup.object().shape({
		mobile_number: Yup.string()
			.typeError("Mobile number must be numeric")
			.min(9, "Mobile number must be 9 digits and above")
			.required("EcoCash mobile number for auto-deduction is required"),
		currency: Yup.string().required("Currency is required"),
		// subscription: Yup.string().required("Subscription is required"),
	});

	const { mutateAsync, isLoading: isApplyingAutoDeduction } = useApplyAutoDeduction();

	async function applyAutoDeduction(values) {
		const data = {
			...values,
			payment_method:
				values?.currency === "USD"
					? activePaymentMethods.ecocashUsd.name
					: activePaymentMethods.ecocashZwl.name,
		};
		await mutateAsync(data);
	}

	const { mutateAsync: cancelAutoDeduction, isLoading: isCancelling } = useCancelAutoDeductions();

	async function handleCancelAutoDeduction() {
		await cancelAutoDeduction();
	}

	// console.log({ recurringSubscription });

	return (
		<AccountPage>
			<AccountLayout isLoading={isLoadingProfile || isLoadingSubscriptions} profile={profile?.data}>
				<div className="space-y-6">
					<div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
						<p className="text-sm text-blue-800">
							Auto deductions allow your subscription to renew automatically by deducting the
							subscription amount from your EcoCash wallet when your active subscription expires.
						</p>
					</div>

					{recurringSubscription && (
						<div className="p-4 bg-green-50 border border-green-200 rounded-lg">
							<p className="text-sm text-green-800 mb-3">
								Your account is currently set to auto-deduct{" "}
								<span className="font-bold">
									{formatRecordedMoney(priceInCurrency(recurringSubscription?.prices, recurringSubscription?.pivot?.currency, recurringSubscription?.cost, recurringSubscription?.zwl_price), recurringSubscription?.pivot?.currency)}
								</span>{" "}
								from your{" "}
								<span className="font-bold">
									{getPaymentMethodLabel(recurringSubscription?.pivot?.payment_method)}
								</span>{" "}
								wallet every{" "}
								<span className="font-bold">
									{recurringSubscription?.duration} {recurringSubscription?.time_unit}{" "}
								</span>{" "}
								after your subscription lapses
							</p>

							<div className="flex items-center gap-3">
								<p className="text-sm text-green-800">Cancel auto-deductions?</p>
								<button
									type="button"
									onClick={() => handleCancelAutoDeduction()}
									disabled={isCancelling}
									className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center gap-2 text-sm"
								>
									Yes, Cancel
									{isCancelling && <AiOutlineLoading3Quarters className="animate-spin" size={16} />}
								</button>
							</div>
						</div>
					)}

					<div className="bg-white border border-gray-200 rounded-lg p-6">
						<h3 className="text-lg font-semibold mb-2">Apply/Update Auto Deduction Settings</h3>
						<p className="text-gray-600 mb-6">
							Choose the subscription plan that you want to apply auto-deductions to.
						</p>

						<Formik
							initialValues={initialValues}
							enableReinitialize
							validationSchema={validate}
							onSubmit={(data) => applyAutoDeduction(data)}
						>
							{() => (
								<Form className="space-y-4">
									<AccountField
										autoFocus
										label={"EcoCash mobile number * (e.g 0783580001)"}
										type="number"
										name="mobile_number"
									/>

									<AccountField
										type="select"
										name="currency"
										label="Currency *"
										options={currencies}
									/>

									<AccountField
										type="select"
										name="subscription"
										label="Subscription to apply auto-deduction *"
										options={subscriptionChoices}
									/>

									<div className="pt-4">
										<button
											type="submit"
											className="account-button"
											disabled={isApplyingAutoDeduction}
										>
											Save Settings
											{isApplyingAutoDeduction && (
												<AiOutlineLoading3Quarters size={20} className="animate-spin" />
											)}
										</button>
									</div>
								</Form>
							)}
						</Formik>
					</div>
				</div>
			</AccountLayout>
		</AccountPage>
	);
};

export default AutoDeductions;
